/* =========================================================
   LA COLECȚIE — HERO + BUY BOX
   Gallery dots, variant cards, review mini-slider, mobile
   sticky bar and the delivery estimate.
   ========================================================= */

(() => {
  if (customElements.get('lc-product-hero')) return;

  const DAY = 86400000;

  function todayIn(timeZone) {
    const parts = {};

    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date()).forEach((part) => {
      if (part.type !== 'literal') parts[part.type] = Number(part.value);
    });

    return {
      date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12)),
      hour: parts.hour % 24
    };
  }

  const addDays = (date, count) => new Date(date.getTime() + count * DAY);

  const isBusinessDay = (date) => date.getUTCDay() >= 1 && date.getUTCDay() <= 5;

  function addBusinessDays(date, count) {
    let result = date;
    let added = 0;

    while (added < count) {
      result = addDays(result, 1);
      if (isBusinessDay(result)) added++;
    }

    return result;
  }

  function nextWeekday(date, weekday) {
    let result = addDays(date, 1);
    while (result.getUTCDay() !== weekday) result = addDays(result, 1);
    return result;
  }

  // Production takes N business days (more after the cutoff hour). If that would
  // run over a weekend, the parcel ships on the fixed post-weekend day instead.
  // Courier transit is added on top.
  function estimateDelivery(options) {
    const now = todayIn(options.timeZone);
    const productionDays = now.hour < options.cutoffHour ? options.daysBeforeCutoff : options.daysAfterCutoff;
    let shipDate = null;

    if (isBusinessDay(now.date)) {
      const candidate = addBusinessDays(now.date, productionDays);
      if (Math.round((candidate - now.date) / DAY) === productionDays) shipDate = candidate;
    }

    if (!shipDate) shipDate = nextWeekday(now.date, options.weekendShipDay);

    return addBusinessDays(shipDate, options.transitDays);
  }

  function formatDate(date, locale) {
    const text = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    }).format(date);

    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // Short, locale-aware range such as "Thu, 15 Oct – Tue, 20 Oct".
  function formatDateRange(from, to, locale) {
    const format = new Intl.DateTimeFormat(locale, {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
    const text = format.formatRange ? format.formatRange(from, to) : `${format.format(from)} – ${format.format(to)}`;

    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  class LcProductHero extends HTMLElement {
    connectedCallback() {
      this.cleanup = [];
      this.initGallery();
      this.initReviews();
      this.initVariants();
      this.initSticky();
      this.initDelivery();
    }

    disconnectedCallback() {
      (this.cleanup || []).forEach((fn) => fn());
      this.cleanup = [];
    }

    /* ---------- Gallery ---------- */

    initGallery() {
      this.gallery = this.querySelector('[data-lc-gallery]');
      this.dots = Array.from(this.querySelectorAll('[data-lc-dot]'));

      if (!this.gallery || !this.dots.length) return;

      let frame = null;

      this.gallery.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          this.setActiveDot(Math.round(this.gallery.scrollLeft / this.gallery.clientWidth));
          frame = null;
        });
      }, { passive: true });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.scrollGalleryTo(Number(dot.getAttribute('data-lc-dot')));
        });
      });
    }

    setActiveDot(index) {
      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
    }

    scrollGalleryTo(index) {
      this.gallery.scrollTo({ left: this.gallery.clientWidth * index, behavior: 'smooth' });
    }

    /* ---------- Review mini-slider ---------- */

    initReviews() {
      const reviewsRoot = this.querySelector('[data-lc-buy-reviews]');
      if (!reviewsRoot) return;

      const slides = Array.from(reviewsRoot.querySelectorAll('[data-lc-review-slide]'));
      const dots = Array.from(reviewsRoot.querySelectorAll('[data-lc-review-dot]'));
      const delay = (Number(this.dataset.reviewInterval) || 4) * 1000;
      let index = 0;
      let timer = null;

      const show = (next) => {
        if (!slides.length) return;
        index = (next + slides.length) % slides.length;

        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === index;
          slide.classList.toggle('is-active', isActive);

          if (!isActive) {
            const video = slide.querySelector('video');
            if (video && !video.paused) video.pause();
          }
        });

        dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === index;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      };

      const restart = () => {
        clearInterval(timer);
        if (slides.length < 2) return;
        timer = setInterval(() => show(index + 1), delay);
      };

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          show(Number(dot.getAttribute('data-lc-review-dot')));
          restart();
        });
      });

      show(0);
      restart();
      this.cleanup.push(() => clearInterval(timer));
    }

    /* ---------- Variant cards ---------- */

    initVariants() {
      this.variantInput = this.querySelector('[data-lc-variant-input]');
      this.price = this.querySelector('[data-lc-price]');
      this.stickyPrice = this.querySelector('[data-lc-sticky-price]');
      this.stickyLabel = this.querySelector('[data-lc-sticky-label]');
      this.stickyLabels = this.readJSON('[data-lc-sticky-labels]');

      const radios = Array.from(this.querySelectorAll('[data-lc-variant-radio]'));
      const cards = Array.from(this.querySelectorAll('[data-lc-variant-card]'));

      radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;

          cards.forEach((card) => card.classList.remove('is-selected'));
          const selectedCard = radio.closest('[data-lc-variant-card]');
          if (selectedCard) selectedCard.classList.add('is-selected');

          if (this.variantInput) {
            this.variantInput.value = radio.value;
            this.variantInput.dispatchEvent(new Event('input', { bubbles: true }));
            this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const newPrice = radio.getAttribute('data-price');
          if (newPrice && this.price) this.price.textContent = newPrice;

          this.updateSticky(radio);
          this.showVariantMedia(radio.getAttribute('data-media-id'));
        });
      });

      // Keep the sticky bar in sync with whatever the buy box preselected.
      const checked = radios.find((radio) => radio.checked);
      if (checked) this.updateSticky(checked);
    }

    updateSticky(radio) {
      const label = this.stickyLabels[String(radio.value)];
      const price = radio.getAttribute('data-price');

      if (label && this.stickyLabel) this.stickyLabel.textContent = label;
      if (price && this.stickyPrice) this.stickyPrice.textContent = price;
    }

    showVariantMedia(mediaId) {
      if (!mediaId || !this.gallery) return;

      const slides = Array.from(this.gallery.querySelectorAll('[data-lc-slide]'));
      const targetIndex = slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
      if (targetIndex === -1) return;

      this.scrollGalleryTo(targetIndex);
      this.setActiveDot(targetIndex);
    }

    readJSON(selector) {
      const node = this.querySelector(selector);
      if (!node) return {};

      try {
        return JSON.parse(node.textContent) || {};
      } catch (error) {
        return {};
      }
    }

    /* ---------- Mobile sticky bar ---------- */

    initSticky() {
      const sticky = this.querySelector('[data-lc-sticky]');
      const mainCTA = this.querySelector('.lc-primary-cta');

      if (!sticky || !mainCTA || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver((entries) => {
        const visible = entries[0] && entries[0].isIntersecting;
        sticky.classList.toggle('is-visible', !visible);
      }, { threshold: 0.2 });

      observer.observe(mainCTA);
      this.cleanup.push(() => observer.disconnect());
    }

    /* ---------- Delivery estimate ---------- */

    initDelivery() {
      const nodes = this.querySelectorAll('[data-lc-delivery-date]');
      if (!nodes.length) return;

      const data = this.dataset;
      const number = (value, fallback) => (value === undefined || value === '' ? fallback : Number(value));

      try {
        const options = {
          timeZone: data.deliveryTimezone || 'Europe/Bucharest',
          cutoffHour: number(data.deliveryCutoff, 13),
          daysBeforeCutoff: number(data.deliveryDaysBefore, 1),
          daysAfterCutoff: number(data.deliveryDaysAfter, 2),
          weekendShipDay: number(data.deliveryWeekendDay, 2),
          transitDays: number(data.deliveryTransit, 1)
        };
        const date = estimateDelivery(options);
        const maxTransit = number(data.deliveryTransitMax, options.transitDays);
        const locale = data.deliveryLocale || 'ro-RO';

        const formatted = maxTransit > options.transitDays
          ? formatDateRange(date, estimateDelivery({ ...options, transitDays: maxTransit }), locale)
          : formatDate(date, locale);
        nodes.forEach((node) => { node.textContent = formatted; });
      } catch (error) {
        // Invalid timezone or locale in the settings: keep the snippet's fallback text.
      }
    }
  }

  customElements.define('lc-product-hero', LcProductHero);
})();
